/**
 * Vendor/event calendar (backbonecraftco-com#23): renders the live
 * CraftCore Market Event schedule as a 12-month rolling, month-by-month
 * grid. Data comes straight from backbone-craftcore-erpnext's public,
 * guest-allowed RPC (craftcore.market_calendar.get_upcoming_events) --
 * no data duplicated into this theme, no second system to keep in sync.
 * See craftcore/market_calendar.py in that repo for the exact response
 * shape and the field-level allowlist it enforces server-side.
 *
 * Cross-origin fetch, not a Shopify App Proxy: this is a pure public GET
 * of already-public event data (no write, no need to hide the backend
 * URL, no signature to verify), matching market_calendar.py's own
 * "Guest-read pattern... no App Proxy HMAC needed" precedent and
 * craftcore.local_delivery_tracking's existing guest-RPC pattern. This
 * needs erp.backbonecraftco.com to send an Access-Control-Allow-Origin
 * header permitting this storefront's origin -- see this repo's PR
 * description / commit message for the exact manual step if that isn't
 * enabled yet.
 *
 * Clicking an event chip navigates to a dedicated detail page (follow-up
 * to #23) rather than toggling an inline details panel -- see
 * buildEventChip() below and assets/vendor-event-detail.js.
 */

import { parseLocalDate, formatDateRange, formatTimeRange, formatLocation } from '@theme/vendor-event-format';

const API_URL = 'https://erp.backbonecraftco.com/api/method/craftcore.market_calendar.get_upcoming_events';
const EVENT_DETAIL_PAGE_URL = '/pages/vendor-events';
const MONTHS_IN_WINDOW = 12;
const MONTH_QUERY_PARAM = 'month';
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Formats a `{ year, month }` (month is 0-indexed) as "YYYY-MM" for the
 * `?month=` query param. */
function formatMonthParam(year, month) {
  return `${year}-${String(month + 1).padStart(2, '0')}`;
}

/** Parses a "YYYY-MM" query param value into a zero-based month offset
 * relative to `today`, or null if the value is missing, malformed, or
 * resolves outside the valid `0..MONTHS_IN_WINDOW - 1` window -- callers
 * should fall back to `monthOffset = 0` silently in all of those cases. */
function parseMonthOffsetParam(value, today) {
  if (!value) return null;

  const match = /^(\d{4})-(\d{2})$/.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  if (month < 0 || month > 11) return null;

  const offset =
    (year - today.getFullYear()) * 12 + (month - today.getMonth());
  if (offset < 0 || offset >= MONTHS_IN_WINDOW) return null;

  return offset;
}

class VendorCalendar {
  constructor(root) {
    this.root = root;
    this.statusEl = root.querySelector('[data-vendor-calendar-status]');
    this.gridEl = root.querySelector('[data-vendor-calendar-grid]');
    this.monthLabelEl = root.querySelector('[data-vendor-calendar-month-label]');
    this.prevBtn = root.querySelector('[data-vendor-calendar-prev]');
    this.nextBtn = root.querySelector('[data-vendor-calendar-next]');

    this.events = [];
    this.today = new Date();
    this.today.setHours(0, 0, 0, 0);

    const monthParam = new URLSearchParams(window.location.search).get(MONTH_QUERY_PARAM);
    const parsedOffset = parseMonthOffsetParam(monthParam, this.today);
    this.monthOffset = parsedOffset ?? 0;

    this.prevBtn?.addEventListener('click', () => this.changeMonth(-1));
    this.nextBtn?.addEventListener('click', () => this.changeMonth(1));

    this.loadEvents();
  }

  async loadEvents() {
    try {
      const response = await fetch(API_URL, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);
      const data = await response.json();
      this.events = Array.isArray(data?.message) ? data.message : [];
      this.renderMonth();
    } catch (error) {
      this.showError();
    }
  }

  showError() {
    if (!this.statusEl) return;
    this.statusEl.hidden = false;
    this.statusEl.dataset.state = 'error';
    this.statusEl.textContent = 'Unable to load upcoming events right now. Please check back soon.';
    this.gridEl && (this.gridEl.hidden = true);
  }

  changeMonth(delta) {
    const next = this.monthOffset + delta;
    if (next < 0 || next >= MONTHS_IN_WINDOW) return;
    this.monthOffset = next;
    this.updateMonthQueryParam();
    this.renderMonth();
  }

  /** Reflects the currently-viewed month in this page's own URL (via
   * `history.replaceState`, no navigation/reload) so that pressing the
   * browser Back button after following an event chip -- or simply
   * reloading/sharing the calendar URL -- restores the same month
   * instead of always resetting to the current one. */
  updateMonthQueryParam() {
    const viewDate = new Date(this.today.getFullYear(), this.today.getMonth() + this.monthOffset, 1);
    const url = new URL(window.location.href);
    url.searchParams.set(MONTH_QUERY_PARAM, formatMonthParam(viewDate.getFullYear(), viewDate.getMonth()));
    window.history.replaceState(window.history.state, '', url);
  }

  eventsForMonth(year, month) {
    return this.events.filter((event) => {
      if (!event.start_date) return false;
      const start = parseLocalDate(event.start_date);
      return start.getFullYear() === year && start.getMonth() === month;
    });
  }

  renderMonth() {
    if (!this.gridEl) return;
    this.statusEl && (this.statusEl.hidden = true);
    this.gridEl.hidden = false;

    const viewDate = new Date(this.today.getFullYear(), this.today.getMonth() + this.monthOffset, 1);
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();

    if (this.monthLabelEl) {
      this.monthLabelEl.textContent = `${MONTH_NAMES[month]} ${year}`;
    }
    if (this.prevBtn) this.prevBtn.disabled = this.monthOffset === 0;
    if (this.nextBtn) this.nextBtn.disabled = this.monthOffset === MONTHS_IN_WINDOW - 1;

    const monthEvents = this.eventsForMonth(year, month);
    const eventsByDay = new Map();
    monthEvents.forEach((event) => {
      const day = parseLocalDate(event.start_date).getDate();
      const bucket = eventsByDay.get(day) || [];
      bucket.push(event);
      eventsByDay.set(day, bucket);
    });

    const firstWeekday = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    this.gridEl.innerHTML = '';

    WEEKDAY_LABELS.forEach((label) => {
      const headerCell = document.createElement('div');
      headerCell.className = 'vendor-calendar__weekday';
      headerCell.textContent = label;
      this.gridEl.appendChild(headerCell);
    });

    for (let i = 0; i < firstWeekday; i += 1) {
      const blank = document.createElement('div');
      blank.className = 'vendor-calendar__day vendor-calendar__day--empty';
      this.gridEl.appendChild(blank);
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      this.gridEl.appendChild(this.buildDayCell(day, eventsByDay.get(day) || []));
    }
  }

  buildDayCell(day, events) {
    const cell = document.createElement('div');
    cell.className = 'vendor-calendar__day';

    const dayNumber = document.createElement('span');
    dayNumber.className = 'vendor-calendar__day-number';
    dayNumber.textContent = String(day);
    cell.appendChild(dayNumber);

    events.forEach((event) => cell.appendChild(this.buildEventChip(event)));
    return cell;
  }

  buildEventChip(event) {
    const chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'vendor-calendar__event';

    const name = document.createElement('span');
    name.className = 'vendor-calendar__event-name';
    name.textContent = event.market_name || 'Event';
    chip.appendChild(name);

    const dateLine = document.createElement('span');
    dateLine.className = 'vendor-calendar__event-date';
    dateLine.textContent = formatDateRange(event);
    chip.appendChild(dateLine);

    // Navigates to the dedicated event detail page (follow-up to #23)
    // rather than toggling an inline details panel. `event.name` is the
    // CraftCore Market Event's own internal identifier -- see this
    // file's header comment and the storefront repo's report on
    // get_upcoming_events() for the current gap around this field.
    chip.addEventListener('click', () => {
      if (!event.name) return;
      this.updateMonthQueryParam();
      window.location.href = `${EVENT_DETAIL_PAGE_URL}?event=${encodeURIComponent(event.name)}`;
    });

    return chip;
  }
}

document.querySelectorAll('[data-vendor-calendar]').forEach((root) => new VendorCalendar(root));
